// src/pages/public/PublicInvoicePage.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicInvoice } from '../../services/api/publicInvoice';
import styles from './PublicInvoicePage.module.css';

const IMAGE_BASE =
  process.env.REACT_APP_IMAGE_BASE_URL ||
  (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');

// ── Tiny helpers ──────────────────────────────────────────────────────────────

const fmt = (amount, currency = 'LKR') =>
  Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (str) => {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const fmtDateTime = (str) => {
  if (!str) return '—';
  return new Date(str).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PaymentMethodLabel = {
  cash: 'Cash',
  card: 'Card',
  bank_transfer: 'Bank Transfer',
  cheque: 'Cheque',
  online: 'Online',
};

// ── Shared invoice table ──────────────────────────────────────────────────────

const ItemsTable = ({ items, currency }) => (
  <table className={styles.itemsTable}>
    <thead>
      <tr>
        <th className={styles.colProduct}>Product</th>
        <th className={styles.colRight}>Qty</th>
        <th className={styles.colRight}>Unit Price</th>
        {items.some((i) => i.lineDiscount > 0) && (
          <th className={styles.colRight}>Discount</th>
        )}
        <th className={styles.colRight}>Total</th>
      </tr>
    </thead>
    <tbody>
      {items.map((item, idx) => (
        <tr key={idx}>
          <td>
            <span className={styles.productName}>{item.productName}</span>
            {item.sku && <span className={styles.sku}> ({item.sku})</span>}
          </td>
          <td className={styles.colRight}>{item.quantity}</td>
          <td className={styles.colRight}>{fmt(item.unitPrice)}</td>
          {items.some((i) => i.lineDiscount > 0) && (
            <td className={styles.colRight}>
              {item.lineDiscount > 0 ? `−${fmt(item.lineDiscount)}` : '—'}
            </td>
          )}
          <td className={styles.colRight}>{fmt(item.lineTotal)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// ── Ecom invoice layout ───────────────────────────────────────────────────────

const EcomInvoice = ({ business, invoice }) => {
  const primaryPhone =
    invoice.phoneNumbers?.find((p) => p.isPrimary) || invoice.phoneNumbers?.[0];
  const otherPhones = invoice.phoneNumbers?.filter(
    (p) => p.phoneNumber !== primaryPhone?.phoneNumber
  ) || [];

  return (
    <>
      {/* Status */}
      <div className={styles.statusRow}>
        <span
          className={styles.statusBadge}
          style={invoice.statusColorCode ? {
            backgroundColor: `${invoice.statusColorCode}20`,
            borderColor: invoice.statusColorCode,
            color: invoice.statusColorCode,
          } : undefined}
        >
          {invoice.status}
        </span>
        <span className={styles.paymentTypeBadge}>{invoice.paymentType}</span>
      </div>

      {/* Customer & delivery */}
      <div className={styles.twoCol}>
        <div className={styles.infoBlock}>
          <h3 className={styles.blockTitle}>Customer Details</h3>
          <p className={styles.infoName}>{invoice.customerName}</p>
          {primaryPhone && <p className={styles.infoLine}>{primaryPhone.phoneNumber}</p>}
          {otherPhones.map((p, i) => (
            <p key={i} className={styles.infoLine}>{p.phoneNumber}</p>
          ))}
          {invoice.address && <p className={styles.infoLine}>{invoice.address}</p>}
          {invoice.city && <p className={styles.infoLine}>{invoice.city}</p>}
        </div>
        <div className={styles.infoBlock}>
          <h3 className={styles.blockTitle}>Delivery Info</h3>
          <p className={styles.infoLine}>
            <strong>Shop:</strong> {invoice.shopName}
          </p>
          {invoice.airwaybillNumber && (
            <p className={styles.infoLine}>
              <strong>Airwaybill:</strong> {invoice.airwaybillNumber}
            </p>
          )}
          {invoice.dispatchedDate && (
            <p className={styles.infoLine}>
              <strong>Dispatched:</strong> {fmtDate(invoice.dispatchedDate)}
            </p>
          )}
          {invoice.deliveryNotes && (
            <p className={styles.infoLine}>
              <strong>Notes:</strong> {invoice.deliveryNotes}
            </p>
          )}
        </div>
      </div>

      {/* Items */}
      <ItemsTable items={invoice.items} currency={business.currency} />

      {/* Totals */}
      <div className={styles.totalsSection}>
        <div className={styles.totalsTable}>
          <div className={styles.totalRow}>
            <span>Subtotal</span>
            <span>{fmt(invoice.totalAmount)}</span>
          </div>
          {invoice.totalDiscount > 0 && (
            <div className={styles.totalRow}>
              <span>Discount</span>
              <span className={styles.discountVal}>−{fmt(invoice.totalDiscount)}</span>
            </div>
          )}
          <div className={styles.totalRow}>
            <span>Delivery</span>
            <span>{fmt(invoice.deliveryCharge)}</span>
          </div>
          {invoice.totalTax > 0 && (
            <div className={styles.totalRow}>
              <span>Tax</span>
              <span>{fmt(invoice.totalTax)}</span>
            </div>
          )}
          <div className={`${styles.totalRow} ${styles.grandTotalRow}`}>
            <span>Grand Total</span>
            <span>{fmt(invoice.grandTotal)}</span>
          </div>
          {invoice.paidAmount > 0 && (
            <>
              <div className={styles.totalRow}>
                <span>Paid</span>
                <span className={styles.paidVal}>{fmt(invoice.paidAmount)}</span>
              </div>
              <div className={styles.totalRow}>
                <span>COD Amount</span>
                <span className={styles.codVal}>{fmt(invoice.codAmount)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {invoice.specialNotes && (
        <div className={styles.notesBlock}>
          <strong>Special Notes:</strong> {invoice.specialNotes}
        </div>
      )}
    </>
  );
};

// ── POS invoice layout ────────────────────────────────────────────────────────

const PosInvoice = ({ business, invoice }) => (
  <>
    {/* Status */}
    <div className={styles.statusRow}>
      <span className={styles.statusBadge}>{invoice.status.replace(/_/g, ' ')}</span>
      <span className={styles.paymentTypeBadge}>{invoice.paymentStatus}</span>
    </div>

    {/* Customer info */}
    <div className={styles.twoCol}>
      <div className={styles.infoBlock}>
        <h3 className={styles.blockTitle}>Customer</h3>
        <p className={styles.infoName}>{invoice.customerName}</p>
        {invoice.customerPhone && (
          <p className={styles.infoLine}>{invoice.customerPhone}</p>
        )}
      </div>
      <div className={styles.infoBlock}>
        <h3 className={styles.blockTitle}>Sale Info</h3>
        <p className={styles.infoLine}>
          <strong>Shop:</strong> {invoice.shopName}
        </p>
        {invoice.cashier && (
          <p className={styles.infoLine}>
            <strong>Cashier:</strong> {invoice.cashier}
          </p>
        )}
        {invoice.completedAt && (
          <p className={styles.infoLine}>
            <strong>Completed:</strong> {fmtDateTime(invoice.completedAt)}
          </p>
        )}
      </div>
    </div>

    {/* Items */}
    <ItemsTable items={invoice.items} currency={business.currency} />

    {/* Totals */}
    <div className={styles.totalsSection}>
      <div className={styles.totalsTable}>
        {invoice.totalDiscount > 0 && (
          <div className={styles.totalRow}>
            <span>Item Discounts</span>
            <span className={styles.discountVal}>−{fmt(invoice.totalDiscount)}</span>
          </div>
        )}
        {invoice.orderDiscount > 0 && (
          <div className={styles.totalRow}>
            <span>Order Discount</span>
            <span className={styles.discountVal}>−{fmt(invoice.orderDiscount)}</span>
          </div>
        )}
        {invoice.loyaltyDiscountAmount > 0 && (
          <div className={styles.totalRow}>
            <span>Loyalty Discount</span>
            <span className={styles.discountVal}>−{fmt(invoice.loyaltyDiscountAmount)}</span>
          </div>
        )}
        {invoice.totalTax > 0 && (
          <div className={styles.totalRow}>
            <span>Tax</span>
            <span>{fmt(invoice.totalTax)}</span>
          </div>
        )}
        <div className={`${styles.totalRow} ${styles.grandTotalRow}`}>
          <span>Grand Total</span>
          <span>{fmt(invoice.grandTotal)}</span>
        </div>
        <div className={styles.totalRow}>
          <span>Paid</span>
          <span className={styles.paidVal}>{fmt(invoice.paidAmount)}</span>
        </div>
        {invoice.balanceAmount > 0 && (
          <div className={styles.totalRow}>
            <span>Balance Due</span>
            <span className={styles.codVal}>{fmt(invoice.balanceAmount)}</span>
          </div>
        )}
      </div>
    </div>

    {/* Payments breakdown */}
    {invoice.payments?.length > 0 && (
      <div className={styles.paymentsBlock}>
        <h3 className={styles.blockTitle}>Payments</h3>
        {invoice.payments.map((p, i) => (
          <div key={i} className={styles.paymentLine}>
            <span>{PaymentMethodLabel[p.method] || p.method}</span>
            <span>{fmt(p.amount)}</span>
          </div>
        ))}
      </div>
    )}

    {invoice.notes && (
      <div className={styles.notesBlock}>
        <strong>Notes:</strong> {invoice.notes}
      </div>
    )}
  </>
);

// ── Main component ────────────────────────────────────────────────────────────

const handlePrint = (title) => {
  if (title) document.title = title;
  document.body.classList.add('printing-invoice');
  // Wait one frame so the class is applied and transitions stop before Chrome captures the page
  requestAnimationFrame(() => {
    window.print();
    document.body.classList.remove('printing-invoice');
  });
};

const PublicInvoicePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid invoice link.');
      setLoading(false);
      return;
    }

    getPublicInvoice(token)
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Invoice not found.');
        setLoading(false);
      });
  }, [token]);

  if (loading) {
    return (
      <div className={styles.centerPage}>
        <div className={styles.spinner} />
        <p>Loading invoice…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.centerPage}>
        <div className={styles.errorIcon}>✕</div>
        <h2>Invoice Not Found</h2>
        <p className={styles.errorMsg}>{error}</p>
      </div>
    );
  }

  const { business, invoice, orderType } = data;
  const logoUrl = business.logo ? `${IMAGE_BASE}/${business.logo}` : null;

  return (
    <div className={styles.page}>
      {/* Actions bar — hidden when printing */}
      <div className={styles.actionsBar}>
        <span className={styles.actionsLabel}>Invoice #{invoice.orderNumber}</span>
        <div className={styles.actionsBtns}>
          <button className={styles.printBtn} onClick={() => handlePrint()}>
            🖨 Print
          </button>
          <button
            className={styles.pdfBtn}
            onClick={() => handlePrint(`Invoice-${invoice.orderNumber}`)}
          >
            ⬇ Download PDF
          </button>
        </div>
      </div>

      {/* Invoice document */}
      <div className={`${styles.invoice} invoice-print-content`}>
        {/* Header */}
        <div className={styles.invoiceHeader}>
          <div className={styles.businessInfo}>
            {logoUrl && (
              <img
                src={logoUrl}
                alt={business.name}
                className={styles.businessLogo}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div>
              <h1 className={styles.businessName}>{business.name}</h1>
              {business.phone && (
                <p className={styles.businessDetail}>{business.phone}</p>
              )}
              {business.address && (
                <p className={styles.businessDetail}>{business.address}</p>
              )}
            </div>
          </div>

          <div className={styles.invoiceMeta}>
            <h2 className={styles.invoiceTitle}>INVOICE</h2>
            <table className={styles.metaTable}>
              <tbody>
                <tr>
                  <td className={styles.metaLabel}>Invoice No.</td>
                  <td className={styles.metaValue}>{invoice.orderNumber}</td>
                </tr>
                <tr>
                  <td className={styles.metaLabel}>Date</td>
                  <td className={styles.metaValue}>{fmtDateTime(invoice.date)}</td>
                </tr>
                {invoice.privateReference && (
                  <tr>
                    <td className={styles.metaLabel}>Reference</td>
                    <td className={styles.metaValue}>{invoice.privateReference}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <hr className={styles.divider} />

        {/* Body — switches on order type */}
        {orderType === 'ecom' ? (
          <EcomInvoice business={business} invoice={invoice} />
        ) : (
          <PosInvoice business={business} invoice={invoice} />
        )}

        {/* Footer */}
        <div className={styles.invoiceFooter}>
          <p>Thank you for your business!</p>
          <p className={styles.footerNote}>
            This is a computer-generated invoice. No signature is required.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicInvoicePage;
